// The picker-wheel mechanics behind the panel's step lists. The focused card sits at a fixed
// vertical spot and the steps travel past it, so only a window of neighbours is ever mounted.
//
// Two lists ride on this: one instance's steps in run order, and the compared pair's combined
// path. What they show in a row differs entirely — one run's reading versus two — so only the
// movement lives here: which slots exist, where the stacks sit, and the keys that turn them.
import { MessageCircleQuestion } from "lucide-react";
// biome-ignore lint/style/useImportType: this bundle compiles JSX with the classic React runtime.
import React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "../../utils";
import { appIconUri } from "./appIcon";
import { ownsArrowKeys, ownsEnterKey } from "./processSidebarUi";

/** Steps kept visible above the focused one — fewer, so the card sits high in the panel. */
export const WHEEL_ROWS_ABOVE = 2;
/** Steps kept visible below the focused one. */
export const WHEEL_ROWS_BELOW = 3;
/** Collapsed row height, in px — fixed so the focused card never moves vertically. */
export const WHEEL_ROW_HEIGHT = 44;
/** How long the neighbours take to slide one row past the focused card. */
const SPIN_MS = 220;
/** Extra time each additional row adds to a multi-row spin, in ms. */
const SPIN_MS_PER_EXTRA_ROW = 55;
/** Ceiling on a spin's duration, so a long jump still lands promptly. */
const SPIN_MS_MAX = 620;
/** Jumps longer than this swap instead of spinning — the wheel would render too many rows. */
const MAX_SPIN_ROWS = 12;
/** The tip's fade, matched by the class below so it leaves the DOM once it is invisible. */
const KEYS_HINT_FADE_MS = 300;

/** Which stack a slot belongs to — the rows above the focused card, or the ones below it. */
type WheelSide = "above" | "below";

type StepWheel = {
  aboveRows: number;
  belowRows: number;
  /** Row indexes mounted above the card, clip-edge buffers included, top to bottom. */
  aboveSlots: number[];
  /** Row indexes mounted below the card, same order. */
  belowSlots: number[];
  /** Shared transform for both stacks: their resting offset, or the spin in flight. */
  stackStyle: React.CSSProperties;
  onStackTransitionEnd: (event: React.TransitionEvent<HTMLElement>) => void;
  /**
   * Whether a slot is parked outside the clip — a row that only exists so the stack has something
   * to slide in from. Buffered rows render inert and out of the a11y tree.
   */
  isBuffered: (index: number, side: WheelSide) => boolean;
};

/**
 * The wheel's movement for a list focused on `focusIndex`. Any focus change spins the wheel by
 * that many rows: the stacks buffer as many rows beyond each clip edge as the jump is long, then
 * travel the whole distance in one continuous move.
 */
export const useStepWheel = (focusIndex: number, totalRows: number): StepWheel => {
  const shownRows = Math.min(totalRows, WHEEL_ROWS_ABOVE + 1 + WHEEL_ROWS_BELOW);
  const firstRow = Math.max(0, Math.min(focusIndex - WHEEL_ROWS_ABOVE, totalRows - shownRows));
  const aboveRows = focusIndex - firstRow;
  const belowRows = shownRows - aboveRows - 1;
  // `settled` is false for the single frame that parks the stacks at the pre-jump offset.
  const [spin, setSpin] = useState<{ delta: number; token: number } | null>(null);
  const [settled, setSettled] = useState(true);
  const previousFocusRef = useRef({ index: focusIndex, aboveRows, totalRows });
  useLayoutEffect(() => {
    const previous = previousFocusRef.current;
    previousFocusRef.current = { index: focusIndex, aboveRows, totalRows };
    const delta = focusIndex - previous.index;
    // At the ends the window stays full and the focused card moves within it. Only spin
    // the neighbours when the card keeps its usual position inside the window.
    if (
      delta === 0 ||
      Math.abs(delta) > MAX_SPIN_ROWS ||
      previous.aboveRows !== aboveRows ||
      previous.totalRows !== totalRows
    ) {
      setSpin(null);
      setSettled(true);
      return;
    }
    setSpin(current => ({ delta, token: (current?.token ?? 0) + 1 }));
    setSettled(false);
  }, [focusIndex, aboveRows, totalRows]);

  // Two frames: the first paints the stacks where they were before the jump (no transition), the
  // second hands them their resting offset, which the transition then animates.
  useLayoutEffect(() => {
    if (settled) return undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setSettled(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [settled, spin?.token]);

  // Rows buffered beyond each clip edge. One at rest, `|delta|` while a spin is in flight, so the
  // steps scrolling past the card exist in the DOM for the whole travel.
  const buffer = spin ? Math.max(1, Math.abs(spin.delta)) : 1;
  const aboveSlots = Array.from(
    { length: aboveRows + 2 * buffer },
    (_, slot) => focusIndex - (aboveRows + buffer) + slot,
  );
  const belowSlots = Array.from(
    { length: belowRows + 2 * buffer },
    (_, slot) => focusIndex - (buffer - 1) + slot,
  );

  const spinMs = spin
    ? Math.min(SPIN_MS + (Math.abs(spin.delta) - 1) * SPIN_MS_PER_EXTRA_ROW, SPIN_MS_MAX)
    : SPIN_MS;
  // Mid-spin the stacks sit `delta` rows back from where they belong; settling closes that gap.
  const offsetRows = spin && !settled ? buffer - spin.delta : buffer;
  const stackStyle: React.CSSProperties = {
    transform: `translateY(-${offsetRows * WHEEL_ROW_HEIGHT}px)`,
    transition:
      spin && settled ? `transform ${spinMs}ms cubic-bezier(0.22, 0.61, 0.36, 1)` : "none",
  };
  // Once the travel is over the buffer collapses back to one row; the resting transform is
  // unchanged by that, so nothing moves.
  const onStackTransitionEnd = (event: React.TransitionEvent<HTMLElement>) => {
    if (event.target === event.currentTarget && event.propertyName === "transform") setSpin(null);
  };
  // Backstop for the cases where no transitionend arrives — reduced motion, or a hidden panel.
  useEffect(() => {
    if (!spin || !settled) return undefined;
    const timer = window.setTimeout(() => setSpin(null), spinMs + 80);
    return () => window.clearTimeout(timer);
  }, [spin, settled, spinMs]);

  const isBuffered = (index: number, side: WheelSide) =>
    side === "above"
      ? index < focusIndex - aboveRows || index >= focusIndex
      : index > focusIndex + belowRows || index <= focusIndex;

  return {
    aboveRows,
    belowRows,
    aboveSlots,
    belowSlots,
    stackStyle,
    onStackTransitionEnd,
    isBuffered,
  };
};

/**
 * Up/down turn the wheel, unless the keystroke belongs to a field or an open menu. Bound in the
 * capture phase: the wheel keeps the arrows even while something above it — an instance menu — is
 * open and would otherwise swallow them on the way back up. `enabled` stands the binding down
 * where a second wheel is on screen and owns the keys instead.
 */
export const useStepWheelKeys = ({
  enabled = true,
  onMove,
  onMoveAcross,
  onToggleDetail,
}: {
  enabled?: boolean;
  /** -1 for up, +1 for down. */
  onMove: (delta: number) => void;
  /**
   * Left/right, where the list has a second axis: the compared pair's two columns. -1 for left,
   * +1 for right. Unbound where the list has only one reading per row.
   */
  onMoveAcross?: (delta: number) => void;
  /** Enter: opens the focused reading's detail, or closes it when it is already open. */
  onToggleDetail?: () => void;
}) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === "Enter") {
        if (!onToggleDetail || ownsEnterKey(event.target)) return;
        event.preventDefault();
        onToggleDetail();
        return;
      }
      const across = event.key === "ArrowLeft" || event.key === "ArrowRight";
      if (across && !onMoveAcross) return;
      if (!across && event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      if (ownsArrowKeys(event.target)) return;
      event.preventDefault();
      if (across) {
        onMoveAcross?.(event.key === "ArrowRight" ? 1 : -1);
        return;
      }
      onMove(event.key === "ArrowDown" ? 1 : -1);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [enabled, onMove, onMoveAcross, onToggleDetail]);
};

/** The tip's life: legible, then fading, then gone from the DOM. */
type StepKeysHintPhase = "off" | "shown" | "fading";

/**
 * The arrow keys are the wheel's main control but nothing on screen says so, so the first wheel
 * opened in a visit gets a tip. `owed` is the visit-wide budget — one tip, whichever wheel is
 * shown first — and `eligible` keeps it from firing on a list with nothing to travel.
 */
export const useStepKeysHint = ({
  owed,
  eligible,
  onShown,
}: {
  owed: boolean;
  eligible: boolean;
  onShown: () => void;
}) => {
  const [phase, setPhase] = useState<StepKeysHintPhase>("off");
  const offered = useRef(false);
  useEffect(() => {
    if (offered.current || !owed || !eligible) return;
    offered.current = true;
    onShown();
    setPhase("shown");
  }, [owed, eligible, onShown]);

  // The tip has no timer of its own: it stands until the arrow keys are used, since that is the
  // one thing it asks for. Only the fade is timed, so the strip leaves the DOM once invisible.
  useEffect(() => {
    if (phase !== "fading") return undefined;
    const timer = window.setTimeout(() => setPhase("off"), KEYS_HINT_FADE_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  // The tip has been taken up — it has nothing left to say.
  const dismiss = useCallback(
    () => setPhase(current => (current === "shown" ? "fading" : current)),
    [],
  );

  return { phase, dismiss };
};

/** Which controls a tip names: one wheel's own keys, or the compared pair's extra two. */
type StepKeysHintVariant = "steps" | "compare";

/** One key group inside a tip — the caps to press, and what pressing them does. */
const HintKeys = ({ caps, label }: { caps: string[]; label: string }) => (
  <span className="inline-flex items-center gap-2">
    <span className="inline-flex items-center gap-1" aria-hidden="true">
      {caps.map(cap => (
        <kbd
          key={cap}
          className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-surface/40 bg-surface/15 px-1 text-[11px] leading-none"
        >
          {cap}
        </kbd>
      ))}
    </span>
    {label}
  </span>
);

/**
 * The tip itself — sits in a reserved strip below the wheel, so it never covers a step's own
 * content, and never intercepts a click on the rows. The compared pair adds its own two keys to
 * the wheel's: crossing between the runs, and opening the focused reading.
 */
export const StepKeysHint = ({
  phase,
  variant = "steps",
}: {
  phase: StepKeysHintPhase;
  variant?: StepKeysHintVariant;
}) => {
  const { t } = useTranslation();
  if (phase === "off") return null;
  return (
    <div
      className={`pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center transition-opacity duration-300 ${
        phase === "shown" ? "opacity-100" : "opacity-0"
      }`}
    >
      <span
        role="status"
        className="inline-flex max-w-[calc(100%-1rem)] flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-full bg-text/95 px-3 py-1.5 text-xs font-medium text-surface shadow-lg"
      >
        <HintKeys caps={["↑", "↓"]} label={t("instance_step_keys_hint")} />
        {variant === "compare" && (
          <>
            <HintKeys caps={["←", "→"]} label={t("instance_step_keys_hint_sides")} />
            <HintKeys caps={["⏎"]} label={t("instance_step_keys_hint_enter")} />
          </>
        )}
      </span>
    </div>
  );
};

/**
 * One clipped stack of neighbour rows. Both wheels frame their rows identically — a fixed-height
 * window the stack slides inside — so the frame belongs here with the movement that drives it.
 */
export const WheelStack = ({
  wheel,
  side,
  renderSlot,
}: {
  wheel: StepWheel;
  side: WheelSide;
  renderSlot: (index: number, side: WheelSide) => React.ReactNode;
}) => {
  const shownRows = side === "above" ? wheel.aboveRows : wheel.belowRows;
  const slots = side === "above" ? wheel.aboveSlots : wheel.belowSlots;
  return (
    <div className="overflow-hidden" style={{ height: shownRows * WHEEL_ROW_HEIGHT }}>
      <ol
        className="wheel-stack"
        style={wheel.stackStyle}
        onTransitionEnd={wheel.onStackTransitionEnd}
      >
        {slots.map(index => renderSlot(index, side))}
      </ol>
    </div>
  );
};

/**
 * A pause, riding the divider it describes. Zero-height and centred on the row border, so the
 * wheel keeps its fixed row height and the reading stays attached to the boundary it belongs to.
 * Hovering turns the reading into the offer that hands the pause to the Ask tab.
 */
export const WaitGapMarker = ({
  seconds,
  from,
  to,
  edge,
  onAsk,
  className = "px-3",
}: {
  seconds: number;
  from: { id: string; name: string; app: string | null | undefined };
  to: { id: string; name: string; app: string | null | undefined };
  /** Which of the row's own borders the pill straddles. */
  edge: "top" | "bottom";
  onAsk: (transition: {
    from: { id: string; name: string; iconUri: string | null };
    to: { id: string; name: string; iconUri: string | null };
    averageWaitSeconds: number;
    transitionsCount: number;
  }) => void;
  /** Horizontal inset, which differs between the full-width and two-column wheels. */
  className?: string;
}) => {
  const { t } = useTranslation();
  const duration = formatDuration(seconds);
  const labels = { duration, from: from.name, to: to.name };
  return (
    <div
      className={`pointer-events-none absolute inset-x-0 z-10 flex justify-end ${className} ${
        edge === "top" ? "top-0 -translate-y-1/2" : "bottom-0 translate-y-1/2"
      }`}
    >
      <button
        type="button"
        onClick={() =>
          onAsk({
            from: { id: from.id, name: from.name, iconUri: appIconUri(from.app ?? null) },
            to: { id: to.id, name: to.name, iconUri: appIconUri(to.app ?? null) },
            averageWaitSeconds: seconds,
            transitionsCount: 1,
          })
        }
        title={t("instance_wait_gap_between", labels)}
        aria-label={t("ask_why_waiting_time_gap", labels)}
        className="group/wait pointer-events-auto inline-flex h-5 items-center gap-1 rounded-full bg-attention-soft px-2 text-[10px] font-medium tabular-nums text-warning-strong shadow-sm transition-all hover:h-6 hover:px-2.5 hover:text-[11px] focus:outline-none focus-visible:h-6 focus-visible:px-2.5 focus-visible:text-[11px] focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        {/* Hovering turns the reading into the offer: same pill, one step larger, asking why. */}
        <span className="group-hover/wait:hidden group-focus-visible/wait:hidden">
          {t("instance_wait_gap", { duration })}
        </span>
        <span className="hidden items-center gap-1 group-hover/wait:inline-flex group-focus-visible/wait:inline-flex">
          {t("ask_why")}
          <MessageCircleQuestion className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      </button>
    </div>
  );
};
